// Elegir la fecha sin teclear (video aprobado): un campo que muestra la fecha en español y abre rodillos con
// inercia (Día/Mes/Año y, si se pide, Hora/Minutos/Segundos), un minicalendario y atajos. Todo con los colores
// del tema. El valor es el mismo de los campos nativos: 'YYYY-MM-DD' o 'YYYY-MM-DDTHH:mm[:ss]'.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as KE, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { MESES, acotar, cambiarParte, celdasMes, diasDelMes, dos, escribirFecha, etiquetaRelativa, finDeMes, inicioDelDia, leerFecha, rangoAnios, textoCorto, textoLargo, type Parte } from '../lib/fechas';
import { Icon } from '../lib/icons';
import { enter, reducedMotion } from '../lib/spring';

const ALTO = 34; // alto de cada renglon del rodillo (px)

type PropsRodillo = { valores: string[]; indice: number; onCambio: (i: number) => void; nombre: string };

// Un rodillo: se arrastra (con inercia), se gira con la rueda del raton o con las flechas y se imanta al renglon.
function Rodillo({ valores, indice, onCambio, nombre }: PropsRodillo) {
  const caja = useRef<HTMLDivElement>(null);
  const pista = useRef<HTMLDivElement>(null);
  const n = valores.length;
  const st = useRef({ pos: indice, vel: 0, meta: indice, modo: 'reposo' as 'reposo' | 'arrastre' | 'inercia' | 'iman', raf: 0, ultY: 0, ultT: 0, recorrido: 0, n });
  const avisar = useRef(onCambio);
  useLayoutEffect(() => {
    avisar.current = onCambio;
  });

  const pinta = useCallback(() => {
    const s = st.current, p = pista.current;
    if (!p) return;
    p.style.transform = `translateY(${(2 - s.pos) * ALTO}px)`;
    const cerca = Math.round(s.pos);
    for (let i = 0; i < p.children.length; i++) {
      const c = p.children[i] as HTMLElement;
      const d = i - s.pos;
      if (Math.abs(d) > 4) {
        if (c.style.opacity !== '0') c.style.opacity = '0';
        continue;
      }
      c.style.transform = `rotateX(${Math.max(-80, Math.min(80, -d * 22))}deg)`;
      c.style.opacity = String(Math.max(0.15, 1 - Math.abs(d) * 0.3));
      c.classList.toggle('sel', cerca === i);
    }
  }, []);

  const acota = (i: number) => Math.max(0, Math.min(st.current.n - 1, i));

  const bucle = useCallback(
    (t: number) => {
      const s = st.current;
      const dt = Math.min(0.032, (t - s.ultT) / 1000 || 0.016);
      s.ultT = t;
      if (s.modo === 'inercia') {
        s.vel *= Math.exp(-4.2 * dt);
        s.pos += s.vel * dt;
        if (s.pos < 0 || s.pos > s.n - 1) s.vel *= 0.6;
        if (Math.abs(s.vel) < 2.4) {
          s.modo = 'iman';
          s.meta = acota(Math.round(s.pos));
        }
      }
      if (s.modo === 'iman') {
        s.vel += (-180 * (s.pos - s.meta) - 24 * s.vel) * dt;
        s.pos += s.vel * dt;
        if (Math.abs(s.pos - s.meta) < 0.002 && Math.abs(s.vel) < 0.02) {
          s.pos = s.meta;
          s.vel = 0;
          s.modo = 'reposo';
          s.raf = 0;
          pinta();
          avisar.current(s.meta);
          return;
        }
      }
      pinta();
      s.raf = requestAnimationFrame(bucle);
    },
    [pinta],
  );

  // Lleva el rodillo a un renglon con el iman (o de golpe si se pidio reducir movimiento).
  const ir = useCallback(
    (meta: number, modo: 'iman' | 'inercia' = 'iman') => {
      const s = st.current;
      cancelAnimationFrame(s.raf);
      s.meta = acota(meta);
      if (reducedMotion()) {
        s.pos = modo === 'inercia' ? acota(Math.round(s.pos)) : s.meta;
        s.meta = s.pos;
        s.vel = 0;
        s.modo = 'reposo';
        pinta();
        avisar.current(s.meta);
        return;
      }
      s.modo = modo;
      s.ultT = performance.now();
      s.raf = requestAnimationFrame(bucle);
    },
    [bucle, pinta],
  );

  // Cambio que viene de afuera (calendario, atajos, otro rodillo que acorto el mes).
  useLayoutEffect(() => {
    const s = st.current;
    s.n = n;
    if (s.modo === 'arrastre' || s.modo === 'inercia') return;
    if (s.modo === 'reposo' && s.pos === indice) {
      pinta();
      return;
    }
    if (s.modo === 'iman' && s.meta === indice) return;
    if (reducedMotion()) {
      s.pos = s.meta = indice;
      pinta();
      return;
    }
    cancelAnimationFrame(s.raf);
    s.meta = Math.max(0, Math.min(n - 1, indice));
    s.modo = 'iman';
    s.ultT = performance.now();
    s.raf = requestAnimationFrame(bucle);
  }, [indice, n, bucle, pinta]);

  // La rueda del raton necesita un oyente no pasivo para no mover la pagina.
  useEffect(() => {
    const el = caja.current;
    if (!el) return;
    const rueda = (e: WheelEvent) => {
      e.preventDefault();
      const s = st.current;
      s.pos = Math.max(-0.4, Math.min(s.n - 0.6, s.pos + e.deltaY / 120));
      ir(Math.round(s.pos));
    };
    el.addEventListener('wheel', rueda, { passive: false });
    return () => el.removeEventListener('wheel', rueda);
  }, [ir]);
  useEffect(() => () => cancelAnimationFrame(st.current.raf), []);

  const tecla = (e: KE<HTMLDivElement>) => {
    const s = st.current;
    const base = s.modo === 'reposo' ? Math.round(s.pos) : s.meta;
    const salto: Record<string, number> = { ArrowDown: 1, ArrowUp: -1, PageDown: 5, PageUp: -5 };
    if (e.key in salto) ir(base + salto[e.key]);
    else if (e.key === 'Home') ir(0);
    else if (e.key === 'End') ir(s.n - 1);
    else return;
    e.preventDefault();
  };

  return (
    <div
      ref={caja}
      className="sf-rueda"
      tabIndex={0}
      role="spinbutton"
      aria-label={nombre}
      aria-valuenow={indice}
      aria-valuemin={0}
      aria-valuemax={n - 1}
      aria-valuetext={valores[indice]}
      onKeyDown={tecla}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        const s = st.current;
        cancelAnimationFrame(s.raf);
        s.modo = 'arrastre';
        s.vel = 0;
        s.recorrido = 0;
        s.ultY = e.clientY;
        s.ultT = performance.now();
        e.currentTarget.setPointerCapture(e.pointerId);
        e.currentTarget.focus({ preventScroll: true });
      }}
      onPointerMove={(e) => {
        const s = st.current;
        if (s.modo !== 'arrastre') return;
        const t = performance.now();
        const d = (s.ultY - e.clientY) / ALTO;
        // Pasado el borde, cuesta mas mover (como una banda elastica).
        const fuera = s.pos < 0 || s.pos > s.n - 1;
        s.pos += fuera ? d * 0.35 : d;
        s.recorrido += Math.abs(s.ultY - e.clientY);
        const dt = (t - s.ultT) / 1000;
        if (dt > 0) s.vel = 0.8 * (d / dt) + 0.2 * s.vel;
        s.ultY = e.clientY;
        s.ultT = t;
        pinta();
      }}
      onPointerUp={(e) => {
        const s = st.current;
        if (s.modo !== 'arrastre') return;
        if (s.recorrido < 4) {
          // Toque sin arrastrar: va al renglon tocado.
          const r = e.currentTarget.getBoundingClientRect();
          ir(Math.round(s.pos + (e.clientY - (r.top + r.height / 2)) / ALTO));
          return;
        }
        ir(Math.round(s.pos), 'inercia');
      }}
      onPointerCancel={() => ir(Math.round(st.current.pos))}
    >
      <div className="sf-pista" ref={pista}>
        {valores.map((x, i) => (
          <div key={i} className="sf-it">
            {x}
          </div>
        ))}
      </div>
    </div>
  );
}

type Props = {
  value: string;
  onChange: (valor: string) => void;
  conHora?: boolean;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  'aria-label'?: string;
  placeholder?: string;
  etiqueta?: string;
  className?: string;
  'data-baliza'?: string;
};

const DOS = (n: number, ini = 0) => Array.from({ length: n }, (_, i) => dos(i + ini));
const HORAS = DOS(24), SESENTA = DOS(60);

export function SelectorFecha({ value, onChange, conHora = false, min, max, disabled, required, id, name, 'aria-label': ariaLabel, placeholder = 'Elegir fecha', etiqueta, className, 'data-baliza': baliza }: Props) {
  const [abierto, setAbierto] = useState(false);
  const boton = useRef<HTMLButtonElement>(null);
  const abiertoRef = useRef(false);
  useEffect(() => {
    abiertoRef.current = abierto;
  }, [abierto]);

  const fecha = leerFecha(value);
  const minD = useMemo(() => leerFecha(min), [min]);
  const maxD = useMemo(() => {
    const m = leerFecha(max);
    // Sin hora, el maximo vale todo ese dia.
    return m && !/T/.test(max ?? '') ? new Date(m.getFullYear(), m.getMonth(), m.getDate(), 23, 59, 59) : m;
  }, [max]);
  const hoy = new Date();

  const cerrar = useCallback((devolverFoco: boolean) => {
    setAbierto(false);
    if (devolverFoco) requestAnimationFrame(() => boton.current?.focus());
  }, []);

  // Escape cierra solo el selector. Se registra al montar (antes que el dialogo que lo contiene) para que el
  // dialogo no se cierre tambien con la misma tecla.
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !abiertoRef.current) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      cerrar(true);
    };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [cerrar]);

  const rel = fecha ? etiquetaRelativa(fecha, hoy, false) : '';
  const pasada = fecha ? fecha.getTime() < inicioDelDia(hoy).getTime() : false;
  const largo = fecha ? textoLargo(fecha, conHora) : 'sin fecha';

  return (
    <span className={`sf ${className ?? ''}`}>
      <button
        ref={boton}
        type="button"
        id={id}
        className={`sf-campo${fecha ? '' : ' vacio'}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        aria-label={ariaLabel ? `${ariaLabel}: ${largo}` : undefined}
        title={fecha ? largo : undefined}
        data-baliza={baliza}
        onClick={() => setAbierto((x) => !x)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !abierto) {
            e.preventDefault();
            setAbierto(true);
          }
        }}
      >
        <Icon name="calendar" />
        <span className="sf-txt">{fecha ? textoCorto(fecha, conHora) : placeholder}</span>
        {fecha ? <span className={`pill ${pasada ? 'bad' : 'acc'}`}>{rel}</span> : null}
      </button>
      {name || required ? <input className="sf-oculto" tabIndex={-1} aria-hidden="true" name={name} value={value} required={required} onChange={() => {}} onFocus={() => boton.current?.focus()} /> : null}
      {abierto ? (
        <Ventana
          ancla={boton}
          inicial={fecha}
          conHora={conHora}
          min={minD}
          max={maxD}
          etiqueta={etiqueta ?? ariaLabel ?? (conHora ? 'Fecha y hora' : 'Fecha')}
          limpiable={!required && !!value}
          cerrar={cerrar}
          fijar={(v) => {
            onChange(v);
            cerrar(true);
          }}
        />
      ) : null}
    </span>
  );
}

type PropsVentana = { ancla: RefObject<HTMLButtonElement | null>; inicial: Date | null; conHora: boolean; min: Date | null; max: Date | null; etiqueta: string; limpiable: boolean; cerrar: (foco: boolean) => void; fijar: (v: string) => void };

function Ventana({ ancla, inicial, conHora, min, max, etiqueta, limpiable, cerrar, fijar }: PropsVentana) {
  const ref = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const hoy = useMemo(() => new Date(), []);
  const [b, setB] = useState<Date>(() => {
    if (inicial) return acotar(inicial, min, max);
    const d = conHora ? new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), hoy.getHours() + 1, 0, 0) : inicioDelDia(hoy);
    return acotar(d, min, max);
  });
  const enfocarDia = useRef(false);

  // Coloca la ventana bajo el campo (o encima si no cabe) y la mantiene pegada al hacer scroll.
  useLayoutEffect(() => {
    const colocar = () => {
      const a = ancla.current, v = ref.current;
      if (!a || !v) return;
      const r = a.getBoundingClientRect();
      const w = v.offsetWidth, h = v.offsetHeight;
      const vw = window.innerWidth, vh = window.innerHeight;
      let top = r.bottom + 6;
      if (top + h > vh - 8) top = r.top - h - 6 >= 8 ? r.top - h - 6 : Math.max(8, vh - h - 8);
      const left = Math.max(8, Math.min(r.left, vw - w - 8));
      setPos((p) => (p && Math.abs(p.top - top) < 1 && Math.abs(p.left - left) < 1 ? p : { top, left }));
    };
    colocar();
    window.addEventListener('resize', colocar);
    window.addEventListener('scroll', colocar, true);
    return () => {
      window.removeEventListener('resize', colocar);
      window.removeEventListener('scroll', colocar, true);
    };
  }, [ancla]);

  // Al abrir: entra con un resorte corto y el foco va al rodillo del dia.
  useEffect(() => {
    enter(ref.current, { y: -6, scale: 0.98, response: 0.28 });
    ref.current?.querySelector<HTMLElement>('.sf-rueda')?.focus({ preventScroll: true });
  }, []);

  // Tocar afuera cierra sin guardar.
  useEffect(() => {
    const fuera = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!t.isConnected || ref.current?.contains(t) || ancla.current?.contains(t)) return;
      cerrar(false);
    };
    document.addEventListener('pointerdown', fuera);
    return () => document.removeEventListener('pointerdown', fuera);
  }, [ancla, cerrar]);

  useEffect(() => {
    if (!enfocarDia.current) return;
    enfocarDia.current = false;
    grid.current?.querySelector<HTMLElement>('button.sel')?.focus();
  }, [b]);

  const cambia = (parte: Parte, valor: number) => setB((d) => acotar(cambiarParte(d, parte, valor), min, max));
  const pon = (d: Date) => setB(acotar(d, min, max));
  const fueraDeRango = (d: Date) => (!!min && inicioDelDia(d).getTime() < inicioDelDia(min).getTime()) || (!!max && inicioDelDia(d).getTime() > max.getTime());

  const anios = rangoAnios(b.getFullYear(), hoy.getFullYear(), min, max);
  const dias = DOS(diasDelMes(b.getFullYear(), b.getMonth()), 1);
  const celdas = celdasMes(b.getFullYear(), b.getMonth());
  const esHoyMes = hoy.getFullYear() === b.getFullYear() && hoy.getMonth() === b.getMonth();

  // Con hora, los atajos traen una hora tipica (5 pm, 8 am, 9 am); sin hora, medianoche.
  const masDias = (n: number, h: number) => new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + n, conHora ? h : 0, 0, 0);
  const atajos: [string, Date][] = [
    [conHora ? 'Hoy 5 pm' : 'Hoy', masDias(0, 17)],
    [conHora ? 'Mañana 8 am' : 'Mañana', masDias(1, 8)],
    ['En una semana', masDias(7, 9)],
    ['Fin de mes', finDeMes(masDias(0, 17))],
  ];

  // Teclado del calendario: flechas mueven el dia; Re Pag / Av Pag cambian de mes.
  const teclaCal = (e: KE<HTMLDivElement>) => {
    const salto: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in salto) pon(new Date(b.getFullYear(), b.getMonth(), b.getDate() + salto[e.key], b.getHours(), b.getMinutes(), b.getSeconds()));
    else if (e.key === 'PageUp') cambia('mes', b.getMonth() - 1);
    else if (e.key === 'PageDown') cambia('mes', b.getMonth() + 1);
    else return;
    e.preventDefault();
    enfocarDia.current = true;
  };

  // Tab no se escapa de la ventana mientras esta abierta.
  const trampa = (e: KE<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !ref.current) return;
    const f = [...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled):not([tabindex="-1"]),[tabindex="0"]')].filter((x) => x.offsetParent !== null);
    if (!f.length) return;
    const i = f.indexOf(document.activeElement as HTMLElement);
    if (e.shiftKey && i <= 0) {
      e.preventDefault();
      f[f.length - 1].focus();
    } else if (!e.shiftKey && i === f.length - 1) {
      e.preventDefault();
      f[0].focus();
    }
  };

  const pasada = b.getTime() < (conHora ? hoy.getTime() : inicioDelDia(hoy).getTime());

  return createPortal(
    <div
      ref={ref}
      className={`sf-pop${conHora ? ' con-hora' : ''}`}
      role="dialog"
      aria-label={`Elegir ${etiqueta.toLowerCase()}`}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden', top: 0, left: 0 }}
      onKeyDown={trampa}
    >
      <div className="sf-lectura" aria-live="polite">
        <span key={`${b.getFullYear()}-${b.getMonth()}-${b.getDate()}`} className="sf-gran mono">
          {b.getDate()}
        </span>
        <span className="sf-lect-txt">
          <span className="lbl">{etiqueta}</span>
          <b>
            {MESES[b.getMonth()]} {b.getFullYear()}
          </b>
          <span className="sf-sem">
            {b.toLocaleDateString('es-CO', { weekday: 'long' })}
            {conHora ? (
              <span className="mono sf-hora">
                {' · '}
                {dos(b.getHours())}:{dos(b.getMinutes())}
                <small>:{dos(b.getSeconds())}</small>
              </span>
            ) : null}
          </span>
        </span>
        <span className={`pill ${pasada ? 'bad' : 'acc'}`}>{etiquetaRelativa(b, hoy, conHora)}</span>
      </div>

      <div className="sf-cuerpo">
        <div className="sf-col">
          <div className="sf-etqs" aria-hidden="true">
            <span>Día</span>
            <span>Mes</span>
            <span>Año</span>
          </div>
          <div className="sf-ruedas">
            <div className="sf-banda" aria-hidden="true" />
            <Rodillo valores={dias} indice={b.getDate() - 1} onCambio={(i) => cambia('dia', i + 1)} nombre="Día" />
            <Rodillo valores={MESES} indice={b.getMonth()} onCambio={(i) => cambia('mes', i)} nombre="Mes" />
            <Rodillo valores={anios.map(String)} indice={Math.max(0, anios.indexOf(b.getFullYear()))} onCambio={(i) => cambia('anio', anios[i])} nombre="Año" />
          </div>
          {conHora ? (
            <>
              <div className="sf-etqs hora" aria-hidden="true">
                <span>Hora</span>
                <span>Minutos</span>
                <span>Segundos</span>
              </div>
              <div className="sf-ruedas hora">
                <div className="sf-banda" aria-hidden="true" />
                <Rodillo valores={HORAS} indice={b.getHours()} onCambio={(i) => cambia('hora', i)} nombre="Hora" />
                <Rodillo valores={SESENTA} indice={b.getMinutes()} onCambio={(i) => cambia('min', i)} nombre="Minutos" />
                <Rodillo valores={SESENTA} indice={b.getSeconds()} onCambio={(i) => cambia('seg', i)} nombre="Segundos" />
              </div>
            </>
          ) : null}
        </div>

        <div className="sf-col">
          <div className="sf-cal">
            <div className="sf-cal-h">
              <button type="button" aria-label="Mes anterior" onClick={() => cambia('mes', b.getMonth() - 1)}>
                ‹
              </button>
              <b>
                {MESES[b.getMonth()]} {b.getFullYear()}
              </b>
              <button type="button" aria-label="Mes siguiente" onClick={() => cambia('mes', b.getMonth() + 1)}>
                ›
              </button>
            </div>
            <div className="sf-grid" ref={grid} role="grid" aria-label={`${MESES[b.getMonth()]} ${b.getFullYear()}`} onKeyDown={teclaCal}>
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((x, i) => (
                <span key={`s${i}`} className="sf-sem-l" aria-hidden="true">
                  {x}
                </span>
              ))}
              {celdas.map((d, i) => {
                if (d == null) return <span key={`v${i}`} />;
                const f = new Date(b.getFullYear(), b.getMonth(), d);
                const sel = d === b.getDate();
                return (
                  <button
                    key={d}
                    type="button"
                    className={`${sel ? 'sel' : ''}${esHoyMes && d === hoy.getDate() ? ' hoy' : ''}`}
                    tabIndex={sel ? 0 : -1}
                    aria-pressed={sel}
                    aria-label={textoLargo(f)}
                    disabled={fueraDeRango(f)}
                    onClick={() => cambia('dia', d)}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="sf-rapidos">
            {atajos.map(([t, d]) => (
              <button key={t} type="button" className="chip" disabled={fueraDeRango(d)} onClick={() => pon(d)}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="sf-pie">
        {limpiable ? (
          <button type="button" className="btn ghost" onClick={() => fijar('')}>
            Quitar fecha
          </button>
        ) : (
          <span />
        )}
        <button type="button" className="btn primary" onClick={() => fijar(escribirFecha(b, conHora))}>
          <Icon name="check" /> Fijar fecha
        </button>
      </div>
    </div>,
    document.body,
  );
}
